import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Volume2 } from "lucide-react";
import api from "@/lib/api";
import { playCorrectDing, playEncourage } from "@/lib/sfx";
import { playSay, playReadingAudio, stopReading } from "@/lib/readingAudio";
import KikoCheer from "@/components/KikoCheer";
import ReadingExercise from "@/components/reading/ReadingExercise";

const WIN = { en: ["Well done!", "Super!", "You did it!", "Brilliant!", "Great reading!"], fr: ["Bravo !", "Super !", "Génial !", "Tu lis bien !", "Champion !"] };
const pickOne = (a) => a[Math.floor(Math.random() * a.length)];

// Runs a list of reading exercises. mode: "practice" (2 tries + Kiko help), "test" (mastery, no hints), "diag" (diagnostic).
export default function ExerciseRunner({ childId, lang, petit, items, mode = "practice", onDone, diagStop, session = "" }) {
  const [i, setI] = useState(0);
  const [wrong, setWrong] = useState([]);
  const [correct, setCorrect] = useState(null);
  const [reveal, setReveal] = useState(null);
  const [help, setHelp] = useState(null);
  const [busy, setBusy] = useState(false);
  const [kiko, setKiko] = useState(null);
  const results = useRef({ firsts: 0, answers: {}, errors: {} });
  const streak = useRef(0);
  const ex = items[i];
  const fr = lang === "fr";
  const done = correct !== null || reveal !== null || (mode !== "practice" && results.current.answers[ex?.id] !== undefined);

  const readIt = async () => {
    await playSay(ex.prompt, lang);
    if (ex.audio && !["ODD_ONE_OUT", "READ_MATCH"].includes(ex.type)) await playReadingAudio(ex.audio, lang);
  };
  useEffect(() => {
    setWrong([]); setCorrect(null); setReveal(null); setHelp(null);
    if (ex && petit && localStorage.getItem("aa_voice_on") !== "off") readIt();
    return () => stopReading();
  }, [i]); // eslint-disable-line react-hooks/exhaustive-deps

  const win = () => {
    streak.current += 1;
    playCorrectDing();
    const msg = pickOne(WIN[lang]);
    setKiko({ n: Date.now(), msg, kind: "win", streak: streak.current });
    if (localStorage.getItem("aa_voice_on") !== "off") setTimeout(() => playSay(msg, lang), 350);
  };

  const answer = async (a) => {
    if (busy || done) return;
    setBusy(true);
    try {
      if (mode === "practice") {
        const attempt = wrong.length + 1;
        const { data } = await api.post(`/reading/${childId}/check`, { exercise_id: ex.id, answer: a, attempt, session });
        if (data.correct) {
          if (attempt === 1) results.current.firsts += 1;
          setCorrect(a);
          win();
        } else {
          streak.current = 0;
          playEncourage();
          setWrong([...wrong, a]);
          setHelp(data.explanation);
          if (data.answer) setReveal(data.answer);
          setKiko({ n: Date.now(), msg: fr ? "Presque ! Écoute encore." : "Almost! Listen again.", kind: "try" });
          if (localStorage.getItem("aa_voice_on") !== "off" && data.explanation) setTimeout(() => playSay(data.explanation, lang), 300);
        }
      } else {
        results.current.answers[ex.id] = a;
        if (mode === "diag") {
          const { data } = await api.post(`/reading/${childId}/${lang}/diagnostic/answer`, { question_id: ex.id, answer: a });
          if (!data.correct) results.current.errors[ex.part] = (results.current.errors[ex.part] || 0) + 1;
        }
        setCorrect(a);
        setKiko({ n: Date.now(), msg: fr ? "C'est noté !" : "Got it!", kind: "win", streak: 1 });
        setTimeout(next, 700);
      }
    } finally {
      setBusy(false);
    }
  };

  const next = () => {
    stopReading();
    const stop = mode === "diag" && diagStop?.(ex, results.current.errors, items, i);
    if (i + 1 >= items.length || stop) onDone(results.current);
    else setI((x) => x + 1);
  };

  if (!ex) return null;
  return (
    <div className="w-full flex flex-col items-center gap-5" data-testid="reading-runner">
      <div className="w-full max-w-2xl flex items-center gap-3">
        <div className="flex-1 h-4 rounded-full bg-white border-2 border-slate-900 overflow-hidden">
          <motion.div className="h-full bg-green-400" animate={{ width: `${((i + (done ? 1 : 0)) / items.length) * 100}%` }} />
        </div>
        <span className="font-bold text-sm" data-testid="reading-runner-count">{i + 1}/{items.length}</span>
      </div>
      <button onClick={readIt} className="flex items-center gap-2 text-xl sm:text-2xl font-bold text-center" data-testid="reading-prompt">
        <Volume2 size={22} className="text-blue-600 shrink-0" /> {ex.prompt}
      </button>
      <AnimatePresence mode="wait">
        <motion.div key={ex.id} initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -40 }} className="w-full flex justify-center">
          <ReadingExercise key={`${ex.id}-${wrong.length}`} ex={ex} lang={lang} petit={petit} onAnswer={answer}
            wrong={wrong} correct={correct} reveal={reveal} locked={done || busy} />
        </motion.div>
      </AnimatePresence>
      {help && mode === "practice" && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} data-testid="reading-kiko-help"
          className="max-w-lg flex items-center gap-3 rounded-3xl bg-yellow-100 border-[3px] border-slate-900 px-4 py-3 shadow-[4px_4px_0_0_#0F172A]">
          <img src="/mascot/kiko.png" alt="Kiko" className="w-14 h-14 shrink-0" />
          <p className="font-semibold">{help}{reveal ? (fr ? ` La bonne réponse : ${reveal}.` : ` The answer is: ${reveal}.`) : ""}</p>
        </motion.div>
      )}
      {mode === "practice" && (correct !== null || reveal !== null) && (
        <motion.button initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} onClick={next} data-testid="reading-next"
          className="rounded-full bg-blue-600 text-white border-[3px] border-slate-900 px-8 h-14 text-xl font-bold shadow-[4px_4px_0_0_#0F172A] inline-flex items-center gap-2">
          {fr ? "Suivant" : "Next"} <ArrowRight />
        </motion.button>
      )}
      <KikoCheer cheer={kiko} />
    </div>
  );
}
